import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Calendar, MoreHorizontal, Plus } from "lucide-react";
import Cookies from "js-cookie";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import ActiveInactiveToggle from "../../../components/ACS-Dashboard/ACS-Blocks/ActiveInactiveToggle";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import TableSecondary from "../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ConfirmDeleteModal from "../../../components/ACS-Dashboard/ACS-Blocks/ConfirmDeleteModal";

import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import {
  addTab,
  removeTab,
  setActiveTab,
} from "../../../redux/slices/resourceSelectionSlice";
import {
  fetchStrategyTypeMetadataCatalogue,
  resourceSelectionApi,
} from "./api/resourceSelectionApi";
import StrategyWorkflowBlock from "./StrategyWorkflowBlock";
import SelectionSimulationPanel from "./SelectionSimulationPanel";
import {
  applyCarrierNamesToRankedRows,
  buildCarrierNameLookupFromOrganizations,
  buildSimulationLaneKey,
  normalizeSimulationLaneKey,
  enrichSimulationCarriersWithKpis,
  extractSimulationRankedCarriers,
  isSimulationCarrierNameMissing,
  formatSimulationStopAddress,
  getTodayOperationDate,
  isSimulationStopValid,
  runWeightedSelectionSimulation,
  simulationLanePathLocationParam,
  simulationStopFromConfig,
  simulationStopLabel,
} from "./selectionSimulationUtils";
import { fetchCarrierKpis } from "../Analytics/KpiPlatform/api/carrierKpiApi";
import { extractAllocationMeta } from "../Shipment/TabContent/ViewShipment/rateShoppingUtils";

import {
  coerceShipmentTypeForTransportMode,
  getShipmentTypeOptionsForMode,
} from "./transportModeShipmentTypes";
import {
  attachMetadataToTemplate,
  extractPagedItemsFromResponse,
  isPersistedParityStrategyId,
  buildParityCarrierConfigRequest,
  buildScoringSpecPayload,
  scoringSpecHasWeights,

  formatParityConfigListItem,
  parityConfigResponseToEditForm,
  strategiesHaveResolvedMetadataIds,
  totalPagesFromPagedPayload,
} from "./parityConfigMappers";
import { getStrategyKpiSaveReadiness, STRATEGY_LIBRARY } from "./strategyConstants";
import {
  getAssignedOfficeCodes,
  getAssignedOfficeOptions,
  getDefaultAssignedOfficeCode,
  resolveOfficeScope,
} from "../../../utils/assignedOffices";
import { useKpiCatalog } from "../Analytics/KpiPlatform/useKpiCatalog";
import KpiLibraryAdmin, {
  CarrierKpiViewPanel,
  CARRIER_KPI_ALL_OFFICES,
  CARRIER_KPI_ALL_BASES,
} from "../Analytics/KpiPlatform/KpiLibraryAdmin";
import { scoringKeyForKpi } from "../Analytics/KpiPlatform/kpiCatalogUtils";
async function fetchAllConfigsForOriginator(originatorId, pageSize = 100) {
  const scope = (originatorId || "").toString().trim();
  if (!scope) return [];

  let list = [];
  for (const startPage of [1, 0]) {
    const response = await resourceSelectionApi.listCarrierSelectionConfigs({
      officeCodes: [scope],
      page: startPage,
      pageSize,
    });
    const chunk = extractPagedItemsFromResponse(response.data);
    if (!chunk.length) continue;

    const totalPages = totalPagesFromPagedPayload(response.data, pageSize, chunk.length);
    list = [...chunk];
    for (let p = startPage + 1; p < startPage + totalPages && p < startPage + 25; p += 1) {
      const next = await resourceSelectionApi.listCarrierSelectionConfigs({
        officeCodes: [scope],
        page: p,
        pageSize,
      });
      list = list.concat(extractPagedItemsFromResponse(next.data));
    }
    break;
  }
  return list;
}

function withOfficeOption(options, code) {
  const normalized = (code || "").toString().trim();
  if (!normalized || options.some((office) => office.officeCode === normalized)) {
    return options;
  }
  return [...options, { officeCode: normalized, label: normalized }];
}

function OriginatorField({ value, onChange, options }) {
  const choices = withOfficeOption(options, value);
  const isSingleOffice = choices.length <= 1;

  return (
    <div>
      <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
        Originator <span className="text-red-500">*</span>
      </label>
      {isSingleOffice ? (
        <input
          className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
          value={value || choices[0]?.officeCode || ""}
          readOnly
          aria-readonly="true"
        />
      ) : (
        <select
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {!value ? <option value="">Select originator</option> : null}
          {choices.map((office) => (
            <option key={office.officeCode} value={office.officeCode}>
              {office.label} ({office.officeCode})
            </option>
          ))}
        </select>
      )}
      <p className="mt-1 text-dense-xs text-gray-500">
        Office code stored as the configuration originator.
      </p>
    </div>
  );
}

function createDefaultDraftStrategy(metadataList) {
  const template = STRATEGY_LIBRARY[0];
  const attached = attachMetadataToTemplate(template, metadataList);
  return {
    ...attached,
    kpis: [],
    id: `draft-${Date.now()}-${template.key}`,
  };
}

/**
 * Updates one rank to an integer 0-100% and rescales the other ranks so totals stay exactly 100%.
 * Non-changed ranks keep their relative shares of the remaining budget (largest-remainder rounding).
 */
function allocationWeightsWithFixedTotal(weights, changedRank, rawNextPct) {
  const list = (weights || []).map((w) => ({
    ...w,
    weightPct: Math.round(Math.max(0, Number(w.weightPct || 0))),
  }));
  const changed = Number(changedRank);
  const idx = list.findIndex((w) => Number(w.rank) === changed);
  if (idx < 0) return weights || [];

  const target = Math.max(
    0,
    Math.min(100, Math.round(Number.isFinite(Number(rawNextPct)) ? Number(rawNextPct) : 0))
  );
  const others = list.filter((_, i) => i !== idx);
  const rest = 100 - target;

  let nextOthers;
  if (others.length === 0) {
    return list.map((w, i) => (i === idx ? { ...w, weightPct: target } : w));
  }
  if (rest <= 0) {
    nextOthers = others.map((w) => ({ ...w, weightPct: 0 }));
  } else {
    const oldSum = others.reduce((s, w) => s + w.weightPct, 0);
    if (oldSum <= 0) {
      const base = Math.floor(rest / others.length);
      let rem = rest - base * others.length;
      nextOthers = others.map((w, i) => ({
        ...w,
        weightPct: base + (i < rem ? 1 : 0),
      }));
    } else {
      const exacts = others.map((w) => (w.weightPct / oldSum) * rest);
      const floors = exacts.map((e) => Math.floor(e));
      let remainder = rest - floors.reduce((a, b) => a + b, 0);
      const order = exacts
        .map((e, i) => ({ i, frac: e - floors[i] }))
        .sort((a, b) => b.frac - a.frac);
      const add = new Array(others.length).fill(0);
      for (let k = 0; k < remainder; k++) {
        add[order[k].i] += 1;
      }
      nextOthers = others.map((w, i) => ({
        ...w,
        weightPct: floors[i] + add[i],
      }));
    }
  }

  const nextByRank = new Map(
    [{ ...list[idx], weightPct: target }, ...nextOthers].map((w) => [Number(w.rank), w])
  );

  return list.map((w) => nextByRank.get(Number(w.rank)) ?? w);
}

const ALL_STATUSES_LABEL = "All Statuses";
const STATUS_FILTER_OPTIONS = [ALL_STATUSES_LABEL, "Active", "Inactive"];

const matchesKpiStatusFilter = (base, filter) => {
  if (!filter || filter === ALL_STATUSES_LABEL) return true;
  const isActive = base?.enabled !== false;
  if (filter === "Active") return isActive;
  if (filter === "Inactive") return !isActive;
  return true;
};

const matchesConfigStatusFilter = (cfg, filter) => {
  if (!filter || filter === ALL_STATUSES_LABEL) return true;
  if (filter === "Active") return cfg?.active !== false;
  if (filter === "Inactive") return cfg?.active === false;
  return true;
};

const readBoolLabel = (value) => (value ? "Yes" : "No");
const toInputDate = (value) => {
  if (!value) return "";
  // The backend sends LocalDate ("2026-06-15") or a zoneless LocalDateTime
  // ("2026-06-15T00:00:00") -- both already start with the date we want. Parsing
  // either through `new Date(...)` and reading it back with local getters (the old
  // approach) shifts the result back a day in any timezone behind UTC: a bare
  // "YYYY-MM-DD" is parsed as UTC midnight, and `.getDate()` then reads local time.
  // Since each edit round-trips this value back through the same input, that shift
  // compounds by a day per save. Read the leading date digits directly instead.
  const isoMatch = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isPersistedStrategyId = (id) =>
  isPersistedParityStrategyId(id) ||
  typeof id === "number" ||
  (typeof id === "string" && /^\d+$/.test(String(id)));

const GEO_HIERARCHY_OPTIONS = ["Any Location", "Location", "City-State", "ZIP", "Country"];

function clearOriginGeoFields() {
  return {
    originGeoValue: "",
    originCity: "",
    originState: "",
    originCountry: "",
    originPostalCode: "",
    originLocationId: "",
  };
}

function clearDestinationGeoFields() {
  return {
    destinationGeoValue: "",
    destCity: "",
    destState: "",
    destCountry: "",
    destPostalCode: "",
    destLocationId: "",
  };
}

/** @param {"origin"|"destination"} side */
function buildLocationLookupPayloadFromConfig(cfg, side) {
  const isO = side === "origin";
  const h = isO ? cfg.originGeoHierarchy : cfg.destinationGeoHierarchy;
  if (!h || h === "Any Location") return null;

  if (h === "Location") {
    const v = (isO ? cfg.originGeoValue : cfg.destinationGeoValue).trim();
    if (!v) return null;
    return { locationName: v };
  }
  if (h === "City-State") {
    const city = (isO ? cfg.originCity : cfg.destCity || "").trim();
    const state = (isO ? cfg.originState : cfg.destState || "").trim();
    const country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    if (!city || !state || !country) return null;
    return { city, state, country };
  }
  if (h === "ZIP") {
    const zip = (isO ? cfg.originPostalCode : cfg.destPostalCode || "").trim();
    const state = (isO ? cfg.originState : cfg.destState || "").trim();
    const country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    if (!zip || !state || !country) return null;
    return { zipCode: zip, state, country };
  }
  if (h === "Country") {
    const v = (isO ? cfg.originGeoValue : cfg.destinationGeoValue).trim();
    if (!v) return null;
    return { country: v };
  }
  return null;
}

function GeoHierarchyFields({
  side,
  cfg,
  onPatch,
  lookupLoading,
  onLookup,
}) {
  const isO = side === "origin";
  const hKey = isO ? "originGeoHierarchy" : "destinationGeoHierarchy";
  const h = cfg[hKey] || "Any Location";
  const title = isO ? "Origin Geo Hierarchy" : "Destination Geo Hierarchy";

  const patchHierarchy = (nextH) =>
    onPatch({
      [hKey]: nextH,
      ...(isO ? clearOriginGeoFields() : clearDestinationGeoFields()),
    });

  return (
    <div>
      <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
        {title} <span className="text-red-500">*</span>
      </label>
      <select
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={h}
        onChange={(e) => patchHierarchy(e.target.value)}
      >
        {GEO_HIERARCHY_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {h !== "Any Location" && (
        <div className="mt-2 space-y-2">
          {h === "Location" && (
            <p className="text-xs text-gray-500">
              Search organization-specific saved locations by name or identifier.
            </p>
          )}
          {h === "City-State" && (
            <>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originCity || "" : cfg.destCity || ""}
                onChange={(e) =>
                  onPatch(isO ? { originCity: e.target.value } : { destCity: e.target.value })
                }
                placeholder="City"
              />
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originState || "" : cfg.destState || ""}
                onChange={(e) =>
                  onPatch(isO ? { originState: e.target.value } : { destState: e.target.value })
                }
                placeholder="State / Province"
              />
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originCountry || "" : cfg.destCountry || ""}
                onChange={(e) =>
                  onPatch(isO ? { originCountry: e.target.value } : { destCountry: e.target.value })
                }
                placeholder="Country"
              />
            </>
          )}
          {h === "ZIP" && (
            <>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originPostalCode || "" : cfg.destPostalCode || ""}
                onChange={(e) =>
                  onPatch(
                    isO ? { originPostalCode: e.target.value } : { destPostalCode: e.target.value }
                  )
                }
                placeholder="ZIP / Postal code"
              />
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originState || "" : cfg.destState || ""}
                onChange={(e) =>
                  onPatch(isO ? { originState: e.target.value } : { destState: e.target.value })
                }
                placeholder="State / Province"
              />
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={isO ? cfg.originCountry || "" : cfg.destCountry || ""}
                onChange={(e) =>
                  onPatch(isO ? { originCountry: e.target.value } : { destCountry: e.target.value })
                }
                placeholder="Country"
              />
            </>
          )}
          {(h === "Location" || h === "Country") && (
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={isO ? cfg.originGeoValue || "" : cfg.destinationGeoValue || ""}
              onChange={(e) =>
                onPatch(
                  isO ? { originGeoValue: e.target.value } : { destinationGeoValue: e.target.value }
                )
              }
              placeholder={h === "Location" ? "Location name / code to search" : "Country"}
            />
          )}
          {(h === "Location" ||
            h === "City-State" ||
            h === "ZIP" ||
            h === "Country") && (
            <div className="flex items-center gap-2">
              <Button variant="outline" text={lookupLoading ? "Looking up..." : "Lookup location"} onClick={onLookup} disabled={lookupLoading} />
              {isO && cfg.originLocationId ? (
                <span className="text-xs text-gray-500">ID: {cfg.originLocationId}</span>
              ) : null}
              {!isO && cfg.destLocationId ? (
                <span className="text-xs text-gray-500">ID: {cfg.destLocationId}</span>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ResourceSelection = () => {
  const location = useLocation();

  const dispatch = useDispatch();
  const resourceSelectionState = useSelector((s) => s.resourceSelection);
  const tabs = useMemo(
    () => [...resourceSelectionState.defaultTabs, ...resourceSelectionState.dynamicTabs],
    [resourceSelectionState.defaultTabs, resourceSelectionState.dynamicTabs]
  );
  const activeTab = resourceSelectionState.activeTab;
  const defaultTabsCount = resourceSelectionState.defaultTabs.length;

  const officeOptions = useMemo(() => getAssignedOfficeOptions(), []);
  const cookieOfficeCode = Cookies.get("officeCode") || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [kpiStatusFilter, setKpiStatusFilter] = useState(ALL_STATUSES_LABEL);
  const [configStatusFilter, setConfigStatusFilter] = useState(ALL_STATUSES_LABEL);
  const [carrierKpiSearchTerm, setCarrierKpiSearchTerm] = useState("");
  const [carrierKpiBaseFilter, setCarrierKpiBaseFilter] = useState(CARRIER_KPI_ALL_BASES);
  const [carrierKpiOfficeFilter, setCarrierKpiOfficeFilter] = useState(CARRIER_KPI_ALL_OFFICES);
  const [carrierKpiMeta, setCarrierKpiMeta] = useState({
    loading: false,
    filteredCount: 0,
    officeOptions: [CARRIER_KPI_ALL_OFFICES],
    refresh: () => {},
  });
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [strategyMetadataList, setStrategyMetadataList] = useState([]);
  const [strategyMetadataLoading, setStrategyMetadataLoading] = useState(true);
  const [strategyMetadataError, setStrategyMetadataError] = useState(null);
  const [isListLoading, setIsListLoading] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const [isRunningSimulation, setIsRunningSimulation] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulationLogs, setSimulationLogs] = useState([]);
  const [simulationRankedCarriers, setSimulationRankedCarriers] = useState([]);

  const clearSimulationResults = useCallback(() => {
    setSimulationResult(null);
    setSimulationLogs([]);
    setSimulationRankedCarriers([]);
  }, []);

  const {
    catalog: kpiCatalog,
    selectionLibrary,
    loading: kpiLibraryLoading,
    saving: kpiLibrarySaving,
    error: kpiLibraryError,
    reload: reloadKpiCatalog,
    saveBaseKpi,
    updateBaseKpi,
    saveSelectionVariant,
    deleteSelectionVariant,
  } = useKpiCatalog();

  const [newConfig, setNewConfig] = useState({
    configName: "",
    originatorId: getDefaultAssignedOfficeCode(),
    active: true,
    effectiveDate: "",
    expiryDate: "",
    shipmentType: "FTL",
    transportMode: "ROAD",
    hazmat: false,
    isRefer: false,
    autoSelectCarrier: true,
    originGeoHierarchy: "Any Location",
    originGeoValue: "",
    originCity: "",
    originState: "",
    originCountry: "",
    originPostalCode: "",
    destinationGeoHierarchy: "Any Location",
    destinationGeoValue: "",
    destCity: "",
    destState: "",
    destCountry: "",
    destPostalCode: "",
    paymentMethod: "Prepaid",
    originLocationId: "",
    destLocationId: "",
  });
  const [newConfigStrategies, setNewConfigStrategies] = useState([]);
  const [editConfigStrategies, setEditConfigStrategies] = useState([]);

  const [editConfig, setEditConfig] = useState(null);

  // Snapshot of the last-saved (or just-loaded) edit-tab config+strategies, so Run Simulation
  // can be disabled while there are unsaved changes. `null` on the new-config tab means "never
  // saved yet" - Run Simulation stays disabled there until the first successful save (which
  // closes the new-config tab and opens a freshly-loaded edit tab anyway).
  const [editConfigSavedSnapshot, setEditConfigSavedSnapshot] = useState(null);

  const [lookupLoading, setLookupLoading] = useState({
    newOrigin: false,
    newDestination: false,
    editOrigin: false,
    editDestination: false,
  });

  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const loadedDetailKeyRef = useRef(null);
  const lastConfigDetailRawRef = useRef(null);

  const carrierKpiDeepLinkRef = useRef(false);
  const strategyMetadataListRef = useRef([]);
  const [isDeletingConfigId, setIsDeletingConfigId] = useState(null);
  const [configPendingDelete, setConfigPendingDelete] = useState(null);
  const [deleteConfigError, setDeleteConfigError] = useState("");

  const [togglingConfigId, setTogglingConfigId] = useState(null);

  const handleLocationLookup = async ({ form, side }) => {
    const isNew = form === "new";
    const cfg = isNew ? newConfig : editConfig;
    if (!cfg) return;

    const payload = buildLocationLookupPayloadFromConfig(cfg, side);

    if (!payload) {
      notify.error({
        title: "Lookup failed",
        message:
          "Enter all required geography fields before lookup (e.g. city, state, and country for City-State).",
      });
      return;
    }

    const key = `${isNew ? "new" : "edit"}${side === "origin" ? "Origin" : "Destination"}`;
    setLookupLoading((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await resourceSelectionApi.searchLocations(payload);
      const data = response?.data;
      const first =
        Array.isArray(data) ? data[0] : Array.isArray(data?.content) ? data.content[0] : data;
      if (!first) {
        notify.error({
          title: "No location found",
          message: "No matching location found for the entered geography.",
        });
        return;
      }

      const locationId = first.locationRef || first.id || first.locationId || "";
      const locationName = first.locationName || first.name || "";

      if (isNew) {
        setNewConfig((prev) =>
          side === "origin"
            ? { ...prev, originLocationId: locationId, originGeoValue: locationName }
            : { ...prev, destLocationId: locationId, destinationGeoValue: locationName }
        );
      } else {
        setEditConfig((prev) =>
          prev
            ? side === "origin"
              ? { ...prev, originLocationId: locationId, originGeoValue: locationName }
              : { ...prev, destLocationId: locationId, destinationGeoValue: locationName }
            : prev
        );
      }

      notify.success({
        title: "Location found",
        message: `Matched location${locationId ? ` (${locationId})` : ""}.`,
      });
    } catch (error) {
      notify.error({
        title: "Lookup failed",
        message: error?.response?.data?.message || "Unable to lookup location.",
      });
    } finally {
      setLookupLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const loadConfigs = useCallback(async () => {
    setIsListLoading(true);
    try {
      const originatorIds = getAssignedOfficeCodes();
      const scopes = originatorIds.length
        ? originatorIds
        : cookieOfficeCode
          ? [cookieOfficeCode]
          : [];

      if (!scopes.length) {
        setConfigs([]);
        return;
      }

      const results = await Promise.allSettled(
        scopes.map((originatorId) => fetchAllConfigsForOriginator(originatorId))
      );

      const merged = [];
      const seen = new Set();
      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        for (const item of result.value) {
          const row = formatParityConfigListItem(item);
          const key = row.id || `${row.officeCode}:${row.name}`;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(row);
        }
      }

      const failures = results.filter((result) => result.status === "rejected");
      if (!merged.length && failures.length) {
        throw failures[0].reason;
      }

      setConfigs(merged);
    } catch (error) {
      notify.error({
        title: "Load failed",
        message:
          error?.response?.data?.message ||
          "Unable to load resource selection configurations.",
      });
      setConfigs([]);
    } finally {
      setIsListLoading(false);
    }
  }, [cookieOfficeCode]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-rs-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdownRow(null);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    setOpenDropdownRow(null);
  }, [location.pathname]);

  useEffect(() => {
    return () => setOpenDropdownRow(null);
  }, []);


  const handleResourceActionClick = (rowKey, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === rowKey ? null : rowKey));
  };

  // Confirmed in the app's own modal rather than window.confirm; the row's dropdown is
  // closed on the way in so it can't sit over the dialog.
  const handleDeleteConfig = (row) => {
    const id = row?.id != null ? String(row.id) : "";
    if (!id) return;
    setDeleteConfigError("");
    setConfigPendingDelete(row);
    setOpenDropdownRow(null);
  };

  const confirmDeleteConfig = async () => {
    const row = configPendingDelete;
    const id = row?.id != null ? String(row.id) : "";
    if (!id) return;
    setIsDeletingConfigId(id);
    setDeleteConfigError("");
    try {
      await resourceSelectionApi.deleteCarrierSelectionConfig(id, {
        originatorId: resolveOfficeScope(row, cookieOfficeCode),
      });
      notify.success({ title: "Deleted", message: "Configuration removed." });
      setConfigPendingDelete(null);

      setOpenDropdownRow(null);
      const tabIdx = tabs.findIndex(
        (t, i) =>
          i >= defaultTabsCount &&
          t.contentType === "configDetail" &&
          String(t.configId) === id
      );
      if (tabIdx >= 0) {
        dispatch(removeTab({ tabIndex: tabIdx }));
      }
      await loadConfigs();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to delete this configuration.";
      // The modal stays up on a failure, so the reason is shown there as well as in the toast.
      setDeleteConfigError(message);
      notify.error({ title: "Delete failed", message });
    } finally {
      setIsDeletingConfigId(null);
    }
  };

  const handleConfigActiveToggle = async (row, isActive) => {
    const id = row?.id != null ? String(row.id) : "";
    if (!id || togglingConfigId === id) return;
    setTogglingConfigId(id);
    try {
      await resourceSelectionApi.patchCarrierConfigActive(
        id,
        { is_active: isActive },
        { originatorId: resolveOfficeScope(row, cookieOfficeCode) }
      );
      notify.success({
        title: isActive ? "Activated" : "Deactivated",
        message: isActive
          ? "Configuration set to active."
          : "Configuration set to inactive.",
      });
      await loadConfigs();
    } catch (error) {
      notify.error({
        title: "Update failed",
        message:
          error?.response?.data?.message ||
          "Unable to update configuration status.",
      });
    } finally {
      setTogglingConfigId(null);
    }
  };

  const metadataScopeCode = useMemo(() => {
    const tab = tabs[activeTab];
    if (tab?.contentType === "newConfig") {
      return (newConfig.originatorId || cookieOfficeCode || "").trim();
    }
    if (tab?.contentType === "configDetail") {
      const fromTab = tab.configOfficeCode;
      if (fromTab && String(fromTab).trim() && fromTab !== "--") {
        return String(fromTab).trim();
      }
    }
    return cookieOfficeCode.trim();
  }, [activeTab, tabs, newConfig.originatorId, cookieOfficeCode]);

  useEffect(() => {
    strategyMetadataListRef.current = strategyMetadataList;
  }, [strategyMetadataList]);

  const goToKpiLibrary = useCallback(() => {
    dispatch(setActiveTab({ index: 0 }));
  }, [dispatch]);

  useEffect(() => {
    const tab = tabs[activeTab];
    if (tab?.contentType === "newConfig" || tab?.contentType === "configDetail") {
      reloadKpiCatalog();
    }
  }, [activeTab, tabs, reloadKpiCatalog]);

  const activeDetailKey = useMemo(() => {
    const tab = tabs[activeTab];
    if (!tab || tab.contentType !== "configDetail" || !tab.configId) return null;
    return `${tab.configId}:${tab.configOfficeCode || ""}`;
  }, [activeTab, tabs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStrategyMetadataLoading(true);
      setStrategyMetadataError(null);
      try {
        const rows = await fetchStrategyTypeMetadataCatalogue(metadataScopeCode);
        if (!cancelled) {
          setStrategyMetadataList(rows);
        }
      } catch (e) {
        console.warn("Strategy type metadata load failed", e);
        if (!cancelled) {
          setStrategyMetadataList([]);
          setStrategyMetadataError(e);
        }
      } finally {
        if (!cancelled) setStrategyMetadataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metadataScopeCode]);

  useEffect(() => {
    const tab = tabs[activeTab];
    if (!tab || tab.contentType !== "newConfig") return;

    setNewConfigStrategies((prev) => {
      if (!strategyMetadataList?.length) {
        if (prev.length) return prev;
        return [createDefaultDraftStrategy([])];
      }
      if (!prev.length) {
        return [createDefaultDraftStrategy(strategyMetadataList)];
      }
      return prev.map((s) => {
        const hasMeta = s.metadataId != null && String(s.metadataId).trim() !== "";
        if (hasMeta) return s;
        const attached = attachMetadataToTemplate(STRATEGY_LIBRARY[0], strategyMetadataList);
        return { ...s, metadataId: attached.metadataId, _parityMeta: attached._parityMeta };
      });
    });
  }, [strategyMetadataList, selectionLibrary, activeTab, tabs]);

  useEffect(() => {
    const tab = tabs[activeTab];
    if (!tab || tab.contentType !== "configDetail" || !editConfig?.id) return;
    if (isDetailsLoading) return;
    if (!strategyMetadataList?.length) return;

    setEditConfigStrategies((prev) =>
      prev.map((s) => {
        const hasMeta = s.metadataId != null && String(s.metadataId).trim() !== "";
        if (hasMeta) return s;
        const attached = attachMetadataToTemplate(STRATEGY_LIBRARY[0], strategyMetadataList);
        return { ...s, metadataId: attached.metadataId, _parityMeta: attached._parityMeta };
      })
    );
  }, [strategyMetadataList, activeTab, tabs, editConfig?.id, isDetailsLoading]);

  const activeTabId = tabs[activeTab]?.id;
  const isKpiLibraryTab = activeTabId === "kpi-library";
  const isCarrierKpiTab = activeTabId === "carrier-kpis";

  useEffect(() => {
    if (activeTabId !== "carrier-kpis") return;
    if (carrierKpiDeepLinkRef.current) {
      carrierKpiDeepLinkRef.current = false;
      return;
    }
    setCarrierKpiBaseFilter(CARRIER_KPI_ALL_BASES);
    setCarrierKpiOfficeFilter(CARRIER_KPI_ALL_OFFICES);
    setCarrierKpiSearchTerm("");
  }, [activeTabId]);

  const carrierKpiBaseOptions = useMemo(
    () => [
      CARRIER_KPI_ALL_BASES,
      ...kpiCatalog
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((base) => base.displayName),
    ],
    [kpiCatalog]
  );

  const carrierKpiBaseFilterLabel = useMemo(() => {
    if (carrierKpiBaseFilter === CARRIER_KPI_ALL_BASES) return CARRIER_KPI_ALL_BASES;
    return (
      kpiCatalog.find((base) => base.kpiCode === carrierKpiBaseFilter)?.displayName ||
      CARRIER_KPI_ALL_BASES
    );
  }, [carrierKpiBaseFilter, kpiCatalog]);

  const handleCarrierKpiMetaChange = useCallback((meta) => {
    setCarrierKpiMeta((prev) => ({ ...prev, ...meta }));
  }, []);

  const handleCarrierKpiBaseFilterSelect = (label) => {
    if (label === CARRIER_KPI_ALL_BASES) {
      setCarrierKpiBaseFilter(CARRIER_KPI_ALL_BASES);
    } else {
      const base = kpiCatalog.find((entry) => entry.displayName === label);
      setCarrierKpiBaseFilter(base?.kpiCode || CARRIER_KPI_ALL_BASES);
    }
    setCarrierKpiSearchTerm("");
  };

  const handleCarrierKpiOfficeFilterSelect = (value) => {
    setCarrierKpiOfficeFilter(value);
    setCarrierKpiSearchTerm("");
  };

  const filteredKpiCatalog = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return kpiCatalog.filter((base) => {
      if (!matchesKpiStatusFilter(base, kpiStatusFilter)) return false;
      if (!q) return true;
      const haystack = [
        base.displayName,
        base.kpiCode,
        base.category,
        base.description,
        ...(base.supportedDimensions || []),
        ...(base.selectionVariants || []).flatMap((v) => [
          v.displayName,
          v.variantCode,
          v.scoringWeightKey,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [kpiCatalog, searchTerm, kpiStatusFilter]);

  // No snapshot yet (still loading, or load failed) counts as dirty - Run Simulation
  // shouldn't be enabled against a config we don't have a confirmed last-saved state for.
  const isEditConfigDirty = useMemo(() => {
    if (!editConfigSavedSnapshot) return true;
    return (
      JSON.stringify({ config: editConfig, strategies: editConfigStrategies }) !==
      editConfigSavedSnapshot
    );
  }, [editConfig, editConfigStrategies, editConfigSavedSnapshot]);

  useEffect(() => {
    if (isDetailsLoading) return;
    if (!isEditConfigDirty) return;
    if (!simulationResult && !simulationLogs.length && !simulationRankedCarriers.length) return;
    clearSimulationResults();
  }, [
    isEditConfigDirty,
    isDetailsLoading,
    simulationResult,
    simulationLogs.length,
    simulationRankedCarriers.length,
    clearSimulationResults,
  ]);

  const editKpiSaveReadiness = useMemo(
    () => getStrategyKpiSaveReadiness(editConfigStrategies[0]?.kpis),
    [editConfigStrategies]
  );

  const newKpiSaveReadiness = useMemo(
    () => getStrategyKpiSaveReadiness(newConfigStrategies[0]?.kpis),
    [newConfigStrategies]
  );


  const filteredConfigs = useMemo(
    () =>
      configs.filter((cfg) => {
        if (!matchesConfigStatusFilter(cfg, configStatusFilter)) return false;
        return cfg.name.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [configs, searchTerm, configStatusFilter]
  );

  const handleStatusFilterSelect = (value) => {
    if (isKpiLibraryTab) {
      setKpiStatusFilter(value);
    } else {
      setConfigStatusFilter(value);
    }
    setSearchTerm("");
  };

  const activeStatusFilter = isKpiLibraryTab ? kpiStatusFilter : configStatusFilter;
  const activeStatusOptions = STATUS_FILTER_OPTIONS;

  const loadDetailForConfig = useCallback(
    async (config, tabIndexOnError, { force = false } = {}) => {
      if (!config?.id) return;

      const loadKey = `${config.id}:${resolveOfficeScope(config, cookieOfficeCode) || ""}`;
      const previousLoadKey = loadedDetailKeyRef.current;
      if (!force && previousLoadKey === loadKey) return;
      loadedDetailKeyRef.current = loadKey;

      setIsDetailsLoading(true);
      setEditConfig(null);
      setEditConfigStrategies([]);
      setEditConfigSavedSnapshot(null);
      setSelectedStrategies([]);
      if (force || previousLoadKey !== loadKey) {
        clearSimulationResults();
      }
      const optimisticSelected =
        config.name != null
          ? config
          : {
              id: config.id,
              name: config.configLabel || `Config ${config.id}`,
              status: "Draft",
              mode: "--",
              type: "--",
              hazmat: "No",
              originGeo: "--",
              originLocation: "--",
              destinationLocation: "--",
            };
      setSelectedConfig(optimisticSelected);

      try {
        const configRes = await resourceSelectionApi.getCarrierSelectionConfig(
          String(config.id),
          { originatorId: resolveOfficeScope(config, cookieOfficeCode) }
        );
        const data = configRes.data;
        lastConfigDetailRawRef.current = data;

        const { editConfig: ec, strategies, selectedConfig: sc } =
          parityConfigResponseToEditForm(data, kpiCatalog);

        const strategiesList =
          strategies.length > 0
            ? strategies
            : [createDefaultDraftStrategy(strategyMetadataListRef.current)];

        setSelectedConfig(sc);
        setSelectedStrategies([]);
        setEditConfigStrategies(strategiesList);
        const loadedEditConfig = {
          ...ec,
          effectiveDate: toInputDate(ec.effectiveDate) || ec.effectiveDate || "",
          expiryDate: toInputDate(ec.expiryDate) || ec.expiryDate || "",
        };
        setEditConfig(loadedEditConfig);
        // Captures the just-loaded (i.e. last-saved) state, including on the reload that
        // follows a successful handleUpdateConfig - so saving re-enables Run Simulation.
        setEditConfigSavedSnapshot(
          JSON.stringify({ config: loadedEditConfig, strategies: strategiesList })
        );
      } catch (error) {
        notify.error({
          title: "Details failed",
          message:
            error?.response?.data?.message || "Unable to load configuration details.",
        });
        setSelectedConfig(null);
        setEditConfig(null);
        if (typeof tabIndexOnError === "number" && tabIndexOnError >= defaultTabsCount) {
          dispatch(removeTab({ tabIndex: tabIndexOnError }));
        }
      } finally {
        setIsDetailsLoading(false);
      }
    },
    [defaultTabsCount, dispatch, cookieOfficeCode, kpiCatalog, clearSimulationResults]
  );

  useEffect(() => {
    if (!editConfig?.id || isDetailsLoading || !lastConfigDetailRawRef.current) return;
    const { strategies } = parityConfigResponseToEditForm(
      lastConfigDetailRawRef.current,
      kpiCatalog
    );
    if (!strategies.length) return;
    const specKpis = strategies[0]?.kpis;
    if (!specKpis?.length) return;
    setEditConfigStrategies((prev) => {
      if (!prev.length) return strategies;
      const next = prev.map((strategy, index) =>
        index === 0 ? { ...strategy, kpis: specKpis } : strategy
      );
      // This effect re-syncs strategy KPIs whenever the KPI catalog (re)loads -- often
      // shortly AFTER a config's own details already loaded and took the saved snapshot.
      // That's a catalog-driven metadata refresh, not a user edit; without re-baselining
      // the snapshot here, editConfigStrategies and editConfigSavedSnapshot fall out of
      // sync the moment a config is opened (before the catalog is ready), permanently
      // marking it dirty and blocking Run Simulation even though nothing was actually edited.
      setEditConfigSavedSnapshot((prevSnapshot) => {
        const prevSerialized = JSON.stringify({ config: editConfig, strategies: prev });
        if (prevSnapshot !== prevSerialized) return prevSnapshot; // already genuinely dirty -- leave it
        return JSON.stringify({ config: editConfig, strategies: next });
      });
      return next;
    });
  }, [kpiCatalog, editConfig?.id, isDetailsLoading]);

  useEffect(() => {
    if (!activeDetailKey) {
      loadedDetailKeyRef.current = null;
      return;
    }
    const t = tabs[activeTab];
    if (!t || t.contentType !== "configDetail" || !t.configId) return;
    loadDetailForConfig(
      {
        id: t.configId,
        name: t.configLabel,
        configLabel: t.configLabel,
        officeCode: t.configOfficeCode,
        originatorId: t.configOfficeCode,
      },
      activeTab
    );
  }, [activeDetailKey, activeTab, tabs, loadDetailForConfig]);

  const handleRemoveTab = (tabIndex) => {
    if (tabIndex < defaultTabsCount) return;
    dispatch(removeTab({ tabIndex }));
  };

  const openCarrierKpiTab = useCallback(
    (base) => {
      if (!base?.kpiCode) return;
      carrierKpiDeepLinkRef.current = true;
      setCarrierKpiBaseFilter(base.kpiCode);
      setCarrierKpiOfficeFilter(CARRIER_KPI_ALL_OFFICES);
      setCarrierKpiSearchTerm("");
      const carrierTabIndex = tabs.findIndex((tab) => tab.id === "carrier-kpis");
      if (carrierTabIndex >= 0) {
        dispatch(setActiveTab({ index: carrierTabIndex }));
      }
    },
    [tabs, dispatch]
  );

  const openConfigInTab = (row) => {
    const existingIdx = tabs.findIndex(
      (t, i) =>
        i >= defaultTabsCount && t.contentType === "configDetail" && t.configId === row.id
    );
    if (existingIdx >= 0) {
      dispatch(setActiveTab({ index: existingIdx }));
      return;
    }
    const label =
      row.name && row.name.length > 26 ? `${row.name.slice(0, 23)}?` : row.name || `Config ${row.id}`;
    dispatch(
      addTab({
        id: `rs-cfg-${row.id}-${Date.now()}`,
        name: label,
        contentType: "configDetail",
        configId: row.id,
        configLabel: row.name || `Config ${row.id}`,
        configOfficeCode: row.officeCode,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const resetNewFormState = () => {
    setNewConfig({
      configName: "",
      originatorId: getDefaultAssignedOfficeCode(),
      active: true,
      effectiveDate: "",
      expiryDate: "",
      shipmentType: "FTL",
      transportMode: "ROAD",
      hazmat: false,
      isRefer: false,
      autoSelectCarrier: true,
      originGeoHierarchy: "Any Location",
      originGeoValue: "",
      originCity: "",
      originState: "",
      originCountry: "",
      originPostalCode: "",
      destinationGeoHierarchy: "Any Location",
      destinationGeoValue: "",
      destCity: "",
      destState: "",
      destCountry: "",
      destPostalCode: "",
      paymentMethod: "Prepaid",
      originLocationId: "",
      destLocationId: "",
    });
    setNewConfigStrategies([createDefaultDraftStrategy(strategyMetadataList)]);
  };

  const openNewConfigTab = () => {
    const existingIdx = tabs.findIndex(
      (t, i) => i >= defaultTabsCount && t.contentType === "newConfig"
    );
    if (existingIdx >= 0) {
      dispatch(setActiveTab({ index: existingIdx }));
      return;
    }
    resetNewFormState();
    dispatch(
      addTab({
        id: `rs-new-${Date.now()}`,
        name: "New configuration",
        contentType: "newConfig",
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const closeCurrentTab = () => {
    if (activeTab >= defaultTabsCount) {
      dispatch(removeTab({ tabIndex: activeTab }));
    }
  };

  const refreshStrategies = useCallback(async () => {
    if (!selectedConfig?.id) return;
    try {
      const res = await resourceSelectionApi.getCarrierSelectionConfig(
        String(selectedConfig.id),
        { originatorId: resolveOfficeScope(selectedConfig, cookieOfficeCode) }
      );
      lastConfigDetailRawRef.current = res.data;
      const { strategies } = parityConfigResponseToEditForm(res.data, kpiCatalog);
      setEditConfigStrategies(strategies);
      setSelectedStrategies([]);
    } catch (e) {
      console.warn("refreshStrategies failed", e);
    }
  }, [selectedConfig?.id, cookieOfficeCode, selectedConfig, kpiCatalog]);

  const handleCreate = async () => {
    const normalizedConfigName = (newConfig.configName || "").trim();
    if (!normalizedConfigName) {
      notify.error({
        title: "Missing fields",
        message: "Configuration Name is required.",
      });
      return;
    }
    const configOriginator = (newConfig.originatorId || "").trim();
    if (!configOriginator) {
      notify.error({
        title: "Missing fields",
        message: "Originator is required.",
      });
      return;
    }
    if (!newConfig.transportMode) {
      notify.error({
        title: "Missing fields",
        message: "Transport mode is required.",
      });
      return;
    }
    if (!newConfig.shipmentType) {
      notify.error({
        title: "Missing fields",
        message: "Shipment type is required for the selected transport mode.",
      });
      return;
    }
    const allowedNewTypes = getShipmentTypeOptionsForMode(newConfig.transportMode);
    if (!allowedNewTypes.some((o) => o.value === newConfig.shipmentType)) {
      notify.error({
        title: "Invalid combination",
        message: "Choose a shipment type that applies to the selected transport mode.",
      });
      return;
    }
    if (!newConfigStrategies.length) {
      notify.error({
        title: "Strategy required",
        message: "Add a strategy to the workflow before saving.",
      });
      return;
    }
    if (strategyMetadataLoading) {
      notify.error({
        title: "Still loading",
        message: "Strategy templates are still loading. Wait a moment and try again.",
      });
      return;
    }
    const canSaveWithoutCatalogue = strategiesHaveResolvedMetadataIds(newConfigStrategies);
    if (!canSaveWithoutCatalogue && !strategyMetadataList.length) {
      const errMsg =
        strategyMetadataError?.response?.data?.message ||
        strategyMetadataError?.message ||
        "";
      notify.error({
        title: strategyMetadataError ? "Templates unavailable" : "Strategy templates required",
        message: strategyMetadataError
          ? errMsg ||
            "Could not load strategy type metadata from the server. Refresh the page or check your connection."
          : "Could not load strategy type metadata (needed to attach the price-based strategy). Set office/originator scope, refresh, or confirm the server exposes /api/resource-selection/config-type-metadata for your account.",
      });
      return;
    }

    if (!newKpiSaveReadiness.canSave) {
      notify.error({
        title: "KPI weights invalid",
        message: newKpiSaveReadiness.message,
      });
      return;
    }


    const newConfigTabIndex = activeTab;
    setIsSaving(true);
    try {
      const body = buildParityCarrierConfigRequest({
        form: { ...newConfig, configName: normalizedConfigName },
        editableStrategies: newConfigStrategies,
        officeCode: configOriginator,
        strategyMetadataList,
        isUpdate: false,
      });
      const response = await resourceSelectionApi.createCarrierSelectionConfig(body);
      const createdId =
        response?.data?.carrier_selection_config_id ?? response?.data?.carrierSelectionConfigId;
      if (newConfig.active && createdId) {
        await resourceSelectionApi.patchCarrierConfigActive(
          String(createdId),
          { is_active: true },
          { originatorId: configOriginator }
        );
      }

      notify.success({
        title: "Created",
        message: "Resource selection configuration and strategies created.",
      });
      await loadConfigs();
      dispatch(removeTab({ tabIndex: newConfigTabIndex }));
      dispatch(
        addTab({
          id: `rs-cfg-${createdId}-${Date.now()}`,
          name: newConfig.configName?.trim() || `Config ${createdId}`,
          contentType: "configDetail",
          configId: String(createdId),
          configLabel: newConfig.configName?.trim() || `Config ${createdId}`,
          configOfficeCode: configOriginator,
          isDynamic: true,
          createdAt: Date.now(),
        })
      );
    } catch (error) {
      const msg =
        error?.message ||
        error?.response?.data?.message ||
        "Unable to create resource selection configuration.";
      notify.error({
        title: "Create failed",
        message: String(msg),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfigStrategiesState = (which, strategyId, updater) => {
    const setStrategies = which === "edit" ? setEditConfigStrategies : setNewConfigStrategies;
    setStrategies((prev) =>
      prev.map((strategy) =>
        strategy.id === strategyId ? { ...strategy, ...updater(strategy) } : strategy
      )
    );
  };

  const handleAllocationModeChange = (which, strategyId, mode) => {
    updateConfigStrategiesState(which, strategyId, () => ({ allocationMode: mode }));
  };

  const handleWeightChange = (which, strategyId, rank, value) => {
    updateConfigStrategiesState(which, strategyId, (strategy) => ({
      allocationWeights: allocationWeightsWithFixedTotal(strategy.allocationWeights, rank, value),
    }));
  };

  const handleAddRank = (which, strategyId) => {
    updateConfigStrategiesState(which, strategyId, (strategy) => {
      const existing = strategy.allocationWeights || [];
      const nextRank = existing.length + 1;
      const palette = ["#3b82f6", "#22c55e", "#a855f7", "#f97316", "#06b6d4"];
      return {
        allocationWeights: [
          ...existing,
          { rank: nextRank, weightPct: 0, color: palette[(nextRank - 1) % palette.length] },
        ],
      };
    });
  };

  const handleRemoveRank = (which, strategyId, rank) => {
    updateConfigStrategiesState(which, strategyId, (strategy) => {
      const filtered = (strategy.allocationWeights || []).filter((weight) => weight.rank !== rank);
      return {
        allocationWeights: filtered.map((weight, idx) => ({ ...weight, rank: idx + 1 })),
      };
    });
  };

  const handleHonourCarrierWorkingHoursChange = (which, strategyId, checked) => {
    updateConfigStrategiesState(which, strategyId, () => ({
      honourCarrierWorkingHours: Boolean(checked),
    }));
  };

  const handleMustRespondChange = (which, strategyId, { value, unit }) => {
    updateConfigStrategiesState(which, strategyId, (strategy) => {
      const next = {};
      if (value !== undefined) {
        next.mustRespondByValue = value === "" ? "" : Number(value);
      }
      if (unit !== undefined) {
        next.mustRespondByUnit = unit;
      }
      return next;
    });
  };

  const handleKpiWeightChange = (which, strategyId, kpiName, rawValue) => {
    const requested = Math.max(0, Math.min(100, Number(rawValue || 0)));
    const setStrategies = which === "edit" ? setEditConfigStrategies : setNewConfigStrategies;

    setStrategies((prev) =>
      prev.map((strategy) => {
        if (strategy.id !== strategyId) return strategy;

        const currentKpis = strategy.kpis || [];
        const targetKpi = currentKpis.find((k) => k.name === kpiName);
        if (!targetKpi) return strategy;

        const otherTotal = currentKpis.reduce(
          (sum, kpi) => (kpi.name === kpiName ? sum : sum + Number(kpi.percent || 0)),
          0
        );
        const maxAllowed = Math.max(0, 100 - otherTotal);
        const nextValue = Math.min(requested, maxAllowed);

        return {
          ...strategy,
          kpis: currentKpis.map((kpi) =>
            kpi.name === kpiName ? { ...kpi, percent: nextValue } : kpi
          ),
        };
      })
    );
  };

  const kpiNameKey = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[^\w]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const handleAddKpiFromLibrary = (which, strategyId, def) => {
    const setStrategies = which === "edit" ? setEditConfigStrategies : setNewConfigStrategies;
    const defKey = def.scoringWeightKey || kpiNameKey(def.name);
    setStrategies((prev) =>
      prev.map((s) => {
        if (s.id !== strategyId) return s;
        const kpis = s.kpis || [];
        if (kpis.some((k) => scoringKeyForKpi(k) === defKey)) return s;
        return {
          ...s,
          kpis: [
            ...kpis,
            {
              name: def.name,
              percent: 0,
              scoringWeightKey: def.scoringWeightKey || defKey,
              variantCode: def.variantCode,
            },
          ],
        };
      })
    );
  };

  const handleRemoveKpi = (which, strategyId, kpiName) => {
    const setStrategies = which === "edit" ? setEditConfigStrategies : setNewConfigStrategies;
    setStrategies((prev) =>
      prev.map((s) =>
        s.id === strategyId
          ? { ...s, kpis: (s.kpis || []).filter((k) => k.name !== kpiName) }
          : s
      )
    );
  };

  const handleReorderKpis = (which, strategyId, orderedKeys) => {
    const setStrategies = which === "edit" ? setEditConfigStrategies : setNewConfigStrategies;
    setStrategies((prev) =>
      prev.map((s) => {
        if (s.id !== strategyId) return s;
        const kpis = s.kpis || [];
        const byKey = Object.fromEntries(kpis.map((k) => [kpiNameKey(scoringKeyForKpi(k)), k]));
        const next = orderedKeys.map((key) => byKey[key]).filter(Boolean);
        return { ...s, kpis: next };
      })
    );
  };

  const handleUpdateConfig = async () => {
    if (!editConfig?.id) return;
    const normalizedConfigName = (editConfig.configName || "").trim();
    if (!normalizedConfigName) {
      notify.error({
        title: "Missing fields",
        message: "Configuration Name is required.",
      });
      return;
    }
    const configOriginator = (editConfig.originatorId || "").trim();
    if (!configOriginator) {
      notify.error({
        title: "Missing fields",
        message: "Originator is required.",
      });
      return;
    }
    if (!editConfig.transportMode) {
      notify.error({
        title: "Missing fields",
        message: "Transport mode is required.",
      });
      return;
    }
    if (!editConfig.shipmentType) {
      notify.error({
        title: "Missing fields",
        message: "Shipment type is required for the selected transport mode.",
      });
      return;
    }
    const allowedEditTypes = getShipmentTypeOptionsForMode(editConfig.transportMode);
    if (!allowedEditTypes.some((o) => o.value === editConfig.shipmentType)) {
      notify.error({
        title: "Invalid combination",
        message: "Choose a shipment type that applies to the selected transport mode.",
      });
      return;
    }
    if (!editConfigStrategies.length) {
      notify.error({
        title: "Strategy required",
        message: "Add a strategy to the workflow before saving.",
      });
      return;
    }
    if (strategyMetadataLoading) {
      notify.error({
        title: "Still loading",
        message: "Strategy templates are still loading. Wait a moment and try again.",
      });
      return;
    }
    const canSaveWithoutCatalogue = strategiesHaveResolvedMetadataIds(editConfigStrategies);
    if (!canSaveWithoutCatalogue && !strategyMetadataList.length) {
      const errMsg =
        strategyMetadataError?.response?.data?.message ||
        strategyMetadataError?.message ||
        "";
      notify.error({
        title: strategyMetadataError ? "Templates unavailable" : "Strategy templates required",
        message: strategyMetadataError
          ? errMsg ||
            "Could not load strategy type metadata from the server. Refresh the page or check your connection."
          : "Could not load strategy type metadata (needed to attach the price-based strategy). Set office/originator scope, refresh, or confirm the server exposes /api/resource-selection/config-type-metadata for your account.",
      });
      return;
    }

    if (!editKpiSaveReadiness.canSave) {
      notify.error({
        title: "KPI weights invalid",
        message: editKpiSaveReadiness.message,
      });
      return;
    }

    setIsUpdatingConfig(true);
    try {
      const strategyKpis = editConfigStrategies[0]?.kpis || [];

      const body = buildParityCarrierConfigRequest({
        form: { ...editConfig, configName: normalizedConfigName },
        editableStrategies: editConfigStrategies,
        officeCode: configOriginator,
        strategyMetadataList,
        isUpdate: true,
      });

      if (strategyKpis.length && !scoringSpecHasWeights(body)) {
        notify.error({
          title: "Scoring spec missing",
          message:
            "KPI weights were not included in the save payload. Reload the configuration and try again.",
        });
        return;
      }


      await resourceSelectionApi.updateCarrierSelectionConfig(
        String(editConfig.id),
        body,
        { originatorId: configOriginator }
      );

      notify.success({ title: "Updated", message: "Configuration and strategies updated." });
      clearSimulationResults();

      await loadConfigs();
      await loadDetailForConfig(
        {
          id: editConfig.id,
          name: editConfig.configName,
          configLabel: editConfig.configName,
          officeCode: configOriginator,
          originatorId: configOriginator,
        },
        null,
        { force: true }
      );
    } catch (error) {
      notify.error({
        title: "Update failed",
        message:
          error?.response?.data?.message || "Unable to update configuration.",
      });
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const handleRunSimulation = async ({
    form = "edit",
    originStop = {},
    destinationStop = {},
    operationDate: simulationOperationDate,
  } = {}) => {
    const cfg = form === "new" ? newConfig : editConfig;
    const strategies = form === "new" ? newConfigStrategies : editConfigStrategies;

    if (!cfg || !strategies.length) {
      notify.error({
        title: "Strategy required",
        message: "Configure a strategy before running simulation.",
      });
      return;
    }

    if (!isSimulationStopValid(originStop) || !isSimulationStopValid(destinationStop)) {
      notify.error({
        title: "Stops required",
        message: "Enter stop name or address for both origin and destination.",
      });
      return;
    }

    const originDisplay = simulationStopLabel(originStop);
    const destinationDisplay = simulationStopLabel(destinationStop);
    const originGraph = simulationLanePathLocationParam(originStop);
    const destinationGraph = simulationLanePathLocationParam(destinationStop);

    setIsRunningSimulation(true);
    clearSimulationResults();

    try {
      const strategy = strategies[0];
      const officeCode = (cfg.originatorId || cookieOfficeCode || "").trim();

      if (!officeCode) {
        notify.error({
          title: "Originator required",
          message: "Select an originator office before running simulation.",
        });
        return;
      }

      const scoringPayload = buildScoringSpecPayload(strategy.kpis || []);
      const persistedConfigId = String(cfg.id || "").trim();

      const routeResponse = await resourceSelectionApi.fetchLanePathsForSimulation({
        origin: originGraph || undefined,
        destination: destinationGraph || undefined,
        originAddress: formatSimulationStopAddress(originStop),
        destinationAddress: formatSimulationStopAddress(destinationStop),
        officeCode,
        operationDate: simulationOperationDate || getTodayOperationDate(),
        paymentMethod: "PREPAID",
        transportMode: cfg.transportMode || undefined,
        shipmentType: cfg.shipmentType || undefined,
        configId: persistedConfigId || undefined,
        scoringSpec: scoringPayload.scoring_spec,
        scoringSpecJson: scoringPayload.scoring_spec_json,
      });

      let rankedFromApi = extractSimulationRankedCarriers(routeResponse.data);

      if (rankedFromApi.some(isSimulationCarrierNameMissing)) {
        try {
          const carriersResponse = await resourceSelectionApi.listOrganizationCarriers();
          const orgLookup = buildCarrierNameLookupFromOrganizations(carriersResponse.data);
          rankedFromApi = applyCarrierNamesToRankedRows(rankedFromApi, orgLookup);
        } catch (nameLookupError) {
          console.warn("Simulation carrier name lookup failed", nameLookupError);
        }
      }

      if (!rankedFromApi.length) {
        notify.error({
          title: "No ranked carriers",
          message:
            routeResponse.data?.message ||
            "No ranked carriers were returned for this origin and destination.",
        });
        return;
      }

      const { laneKey: laneKeyFromApi } = extractAllocationMeta(routeResponse.data);
      const simulationLaneKey = normalizeSimulationLaneKey(
        String(laneKeyFromApi || "").trim() ||
          buildSimulationLaneKey(originStop, destinationStop)
      );

      let rankedForSimulation = rankedFromApi;
      const strategyKpis = strategy.kpis || [];

      if (strategyKpis.length) {
        try {
          const rankedCodes = [
            ...new Set(
              rankedFromApi
                .map((row) => String(row?.carrierCode || "").trim().toUpperCase())
                .filter(Boolean)
            ),
          ];
          const officeCodes = officeCode ? [officeCode] : undefined;
          const carrierKpiBatches = await Promise.all(
            rankedCodes.map((carrierCode) =>
              fetchCarrierKpis({ carrierCode, officeCodes, tenantOffice: officeCode })
            )
          );
          const carrierKpiRows = carrierKpiBatches.flat();
          rankedForSimulation = enrichSimulationCarriersWithKpis(
            rankedFromApi,
            strategyKpis,
            carrierKpiRows,
            kpiCatalog,
            { officeCode, simulationLaneKey }
          );
        } catch (kpiEnrichError) {
          console.warn("Simulation carrier KPI enrichment failed", kpiEnrichError);
        }
      }

      const simulation = runWeightedSelectionSimulation({
        allocationWeights: strategy.allocationWeights,
        rankedCarriers: rankedForSimulation,
        allocationMode: strategy.allocationMode || "WEIGHTED",
      });

      setSimulationLogs(simulation.logs);
      setSimulationResult(simulation);
      setSimulationRankedCarriers(simulation.rankedCarriers);

      notify.success({
        title: "Simulation complete",
        message: `Loaded ${rankedFromApi.length} ranked carriers for ${originDisplay} → ${destinationDisplay}.`,
      });
    } catch (error) {
      notify.error({
        title: "Simulation failed",
        message: error?.response?.data?.message || error?.message || "Unable to run simulation.",
      });
    } finally {
      setIsRunningSimulation(false);
    }
  };

  const renderConfigDetailPane = () => {
    const detailsReady = Boolean(selectedConfig && editConfig && !isDetailsLoading);
    return (
      <div className="h-full min-h-0 bg-custom-bg-purple p-fluid-2 flex flex-col">
        <div className="mb-fluid-2 flex items-center justify-between">
          <h2 className="text-fluid-xl font-semibold text-[#0c1629]">
            Resource Selection: {selectedConfig?.name ?? "?"}
          </h2>
          <Button
            variant="outline"
            onClick={closeCurrentTab}
            text={
              <span className="flex items-center gap-2">
                <ArrowLeft size={16} />
                Close
              </span>
            }
          />
        </div>

        {!detailsReady ? (
          <div className="rounded-xl bg-white p-fluid-3">
            <Loader message="Loading configuration details..." />
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-fluid-2 pr-1">
              <div className="rounded-xl border border-gray-200 bg-white p-fluid-3">
                <p className="mb-fluid-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                  Configuration Basis
                </p>
                <div className="grid grid-cols-1 gap-fluid-2 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Configuration Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={editConfig.configName || ""}
                      onChange={(e) =>
                        setEditConfig((prev) =>
                          prev ? { ...prev, configName: e.target.value } : prev
                        )
                      }
                      placeholder="e.g. Northeast-Expedited-Dray"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <OriginatorField
                      value={editConfig.originatorId || ""}
                      options={officeOptions}
                      onChange={(nextOriginatorId) =>
                        setEditConfig((prev) =>
                          prev ? { ...prev, originatorId: nextOriginatorId } : prev
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Effective Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm"
                        value={editConfig.effectiveDate || ""}
                        onChange={(e) =>
                          setEditConfig((prev) =>
                            prev ? { ...prev, effectiveDate: e.target.value } : prev
                          )
                        }
                        onClick={(e) => e.currentTarget.showPicker?.()}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={(e) => {
                          const input = e.currentTarget.previousSibling;
                          if (input instanceof HTMLInputElement) input.showPicker?.();
                        }}
                        aria-label="Open effective date picker"
                      >
                        <Calendar size={16} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative w-full">
                        <input
                          type="date"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm"
                          value={editConfig.expiryDate || ""}
                          onChange={(e) =>
                            setEditConfig((prev) =>
                              prev ? { ...prev, expiryDate: e.target.value } : prev
                            )
                          }
                          onClick={(e) => e.currentTarget.showPicker?.()}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                          onClick={(e) => {
                            const input = e.currentTarget.previousSibling;
                            if (input instanceof HTMLInputElement) input.showPicker?.();
                          }}
                          aria-label="Open expiry date picker"
                        >
                          <Calendar size={16} />
                        </button>
                      </div>
                      <div className="ml-auto flex flex-col items-end gap-2">
                        <label className="grid grid-cols-[44px_170px] items-center gap-3 text-sm text-[#0c1629]">
                          <button
                            type="button"
                            onClick={() =>
                              setEditConfig((prev) =>
                                prev ? { ...prev, active: !prev.active } : prev
                              )
                            }
                            className={`relative h-6 w-11 rounded-full transition-colors ${
                              editConfig.active ? "bg-[#2563eb]" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                editConfig.active ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </button>
                          <span className="text-left">Is Active</span>
                        </label>
                        <label className="grid grid-cols-[44px_170px] items-center gap-3 text-sm text-[#0c1629]">
                          <button
                            type="button"
                            onClick={() =>
                              setEditConfig((prev) =>
                                prev
                                  ? { ...prev, autoSelectCarrier: !prev.autoSelectCarrier }
                                  : prev
                              )
                            }
                            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                              editConfig.autoSelectCarrier ? "bg-[#2563eb]" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                editConfig.autoSelectCarrier
                                  ? "translate-x-5"
                                  : "translate-x-0.5"
                              }`}
                            />
                          </button>
                          <span className="text-left">Auto Select Carrier</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-fluid-3">
                <p className="mb-fluid-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                  Geographic Routing
                </p>
                <div className="grid grid-cols-1 gap-fluid-2 md:grid-cols-2">
                  <GeoHierarchyFields
                    side="origin"
                    cfg={editConfig}
                    onPatch={(patch) =>
                      setEditConfig((prev) => (prev ? { ...prev, ...patch } : prev))
                    }
                    lookupLoading={lookupLoading.editOrigin}
                    onLookup={() => handleLocationLookup({ form: "edit", side: "origin" })}
                  />
                  <GeoHierarchyFields
                    side="destination"
                    cfg={editConfig}
                    onPatch={(patch) =>
                      setEditConfig((prev) => (prev ? { ...prev, ...patch } : prev))
                    }
                    lookupLoading={lookupLoading.editDestination}
                    onLookup={() => handleLocationLookup({ form: "edit", side: "destination" })}
                  />
                  <div>
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Transport Mode
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={editConfig.transportMode || ""}
                      onChange={(e) => {
                        const nextMode = e.target.value;
                        setEditConfig((prev) => {
                          if (!prev) return prev;
                          if (!nextMode) {
                            return { ...prev, transportMode: "", shipmentType: "" };
                          }
                          const opts = getShipmentTypeOptionsForMode(nextMode);
                          const stillValid = opts.some(
                            (o) => o.value === prev.shipmentType
                          );
                          return {
                            ...prev,
                            transportMode: nextMode,
                            shipmentType: stillValid
                              ? prev.shipmentType
                              : coerceShipmentTypeForTransportMode(nextMode, ""),
                          };
                        });
                      }}
                    >
                      <option value="">Select Mode</option>
                      <option value="ROAD">ROAD</option>
                      <option value="SEA">SEA</option>
                      <option value="AIR">AIR</option>
                      <option value="RAIL">RAIL</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Shipment Type
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={editConfig.shipmentType || ""}
                      onChange={(e) =>
                        setEditConfig((prev) =>
                          prev ? { ...prev, shipmentType: e.target.value } : prev
                        )
                      }
                      disabled={!editConfig.transportMode}
                    >
                      <option value="">
                        {editConfig.transportMode
                          ? "Select Type"
                          : "Select transport mode first"}
                      </option>
                      {getShipmentTypeOptionsForMode(editConfig.transportMode).map(
                        (o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={editConfig.paymentMethod || "Prepaid"}
                      onChange={(e) =>
                        setEditConfig((prev) =>
                          prev ? { ...prev, paymentMethod: e.target.value } : prev
                        )
                      }
                    >
                      <option>Prepaid</option>
                      <option>Collect</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-[#0c1629]">
                        <input
                          type="checkbox"
                          checked={Boolean(editConfig.isRefer)}
                          onChange={(e) =>
                            setEditConfig((prev) =>
                              prev ? { ...prev, isRefer: e.target.checked } : prev
                            )
                          }
                        />
                        Refer
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-[#0c1629]">
                        <input
                          type="checkbox"
                          checked={Boolean(editConfig.hazmat)}
                          onChange={(e) =>
                            setEditConfig((prev) =>
                              prev ? { ...prev, hazmat: e.target.checked } : prev
                            )
                          }
                        />
                        Hazmat Materials
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <StrategyWorkflowBlock
                strategies={editConfigStrategies}
                selectionVariantLibrary={selectionLibrary}
                kpiLibraryLoading={kpiLibraryLoading}
                onGoToKpiLibrary={goToKpiLibrary}
                onKpiWeightChange={(strategyId, kpiName, rawValue) =>
                  handleKpiWeightChange("edit", strategyId, kpiName, rawValue)
                }
                onMustRespondChange={(strategyId, payload) =>
                  handleMustRespondChange("edit", strategyId, payload)
                }
                onHonourCarrierWorkingHoursChange={(strategyId, checked) =>
                  handleHonourCarrierWorkingHoursChange("edit", strategyId, checked)
                }
                onAddKpiFromLibrary={(strategyId, def) =>
                  handleAddKpiFromLibrary("edit", strategyId, def)
                }
                onRemoveKpi={(strategyId, kpiName) =>
                  handleRemoveKpi("edit", strategyId, kpiName)
                }
                onReorderKpis={(strategyId, orderedKeys) =>
                  handleReorderKpis("edit", strategyId, orderedKeys)
                }
                onAllocationWeightChange={(strategyId, rank, value) =>
                  handleWeightChange("edit", strategyId, rank, value)
                }
                onAddAllocationRank={(strategyId) => handleAddRank("edit", strategyId)}
                onRemoveAllocationRank={(strategyId, rank) =>
                  handleRemoveRank("edit", strategyId, rank)
                }
              />

              <SelectionSimulationPanel
                strategies={editConfigStrategies}
                officeCode={editConfig?.originatorId || cookieOfficeCode || ""}
                defaultOriginStop={simulationStopFromConfig(editConfig, "origin")}
                defaultDestinationStop={simulationStopFromConfig(editConfig, "destination")}
                isRunning={isRunningSimulation}
                simulationResult={simulationResult}
                simulationLogs={simulationLogs}
                rankedCarriers={simulationRankedCarriers}
                onRunSimulation={(params) =>
                  handleRunSimulation({ form: "edit", ...params })
                }
                isConfigDirty={isEditConfigDirty}
              />
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-fluid-2 border-t border-gray-200 bg-custom-bg-purple py-2">
              {!editKpiSaveReadiness.canSave ? (
                <p className="max-w-2xl text-sm text-amber-700" role="status">
                  {editKpiSaveReadiness.message}
                </p>
              ) : (
                <span className="min-w-0 flex-1" aria-hidden="true" />
              )}
              <div className="flex shrink-0 items-center gap-fluid-1">
                <Button variant="outline" text="Close" onClick={closeCurrentTab} />
                <Button
                  text={isUpdatingConfig ? "Updating..." : "Update Configuration"}
                  onClick={handleUpdateConfig}
                  disabled={isUpdatingConfig || !editKpiSaveReadiness.canSave}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderNewConfigPane = () => (
      <div className="h-full min-h-0 bg-custom-bg-purple p-fluid-2 flex flex-col">
        <div className="mb-fluid-2 flex items-center justify-between">
          <h2 className="text-fluid-xl font-semibold text-[#0c1629]">
            New Resource Selection Configuration
          </h2>
          <Button
            variant="outline"
            onClick={closeCurrentTab}
            text={
              <span className="flex items-center gap-2">
                <ArrowLeft size={16} />
                Close
              </span>
            }
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-fluid-2 pr-1">
          <div className="rounded-xl border border-gray-200 bg-white p-fluid-3">
            <p className="mb-fluid-2 text-xs font-bold uppercase tracking-wide text-gray-500">
              Configuration Basis
            </p>
            <div className="grid grid-cols-1 gap-fluid-2 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Configuration Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newConfig.configName}
                  onChange={(e) =>
                    setNewConfig((prev) => ({ ...prev, configName: e.target.value }))
                  }
                  placeholder="e.g. Northeast-Expedited-Dray"
                />
              </div>

              <div className="md:col-span-2">
                <OriginatorField
                  value={newConfig.originatorId || ""}
                  options={officeOptions}
                  onChange={(nextOriginatorId) =>
                    setNewConfig((prev) => ({ ...prev, originatorId: nextOriginatorId }))
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Effective Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm"
                    value={newConfig.effectiveDate}
                    onChange={(e) =>
                      setNewConfig((prev) => ({
                        ...prev,
                        effectiveDate: e.target.value,
                      }))
                    }
                    onClick={(e) => e.currentTarget.showPicker?.()}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={(e) => {
                      const input = e.currentTarget.previousSibling;
                      if (input instanceof HTMLInputElement) input.showPicker?.();
                    }}
                    aria-label="Open effective date picker"
                  >
                    <Calendar size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Expiry Date <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative w-full">
                    <input
                      type="date"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm"
                      value={newConfig.expiryDate}
                      onChange={(e) =>
                        setNewConfig((prev) => ({
                          ...prev,
                          expiryDate: e.target.value,
                        }))
                      }
                      onClick={(e) => e.currentTarget.showPicker?.()}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                      onClick={(e) => {
                        const input = e.currentTarget.previousSibling;
                        if (input instanceof HTMLInputElement) input.showPicker?.();
                      }}
                      aria-label="Open expiry date picker"
                    >
                      <Calendar size={16} />
                    </button>
                  </div>
                  <div className="ml-auto flex flex-col items-end gap-2">
                    <label className="grid grid-cols-[44px_170px] items-center gap-3 text-sm text-[#0c1629]">
                      <button
                        type="button"
                        onClick={() =>
                          setNewConfig((prev) => ({ ...prev, active: !prev.active }))
                        }
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                          newConfig.active ? "bg-[#2563eb]" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                            newConfig.active ? "translate-x-5" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className="text-left">Is Active</span>
                    </label>
                    <label className="grid grid-cols-[44px_170px] items-center gap-3 text-sm text-[#0c1629]">
                      <button
                        type="button"
                        onClick={() =>
                          setNewConfig((prev) => ({
                            ...prev,
                            autoSelectCarrier: !prev.autoSelectCarrier,
                          }))
                        }
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                          newConfig.autoSelectCarrier ? "bg-[#2563eb]" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                            newConfig.autoSelectCarrier ? "translate-x-5" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className="text-left">Auto Select Carrier</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-fluid-3">
            <p className="mb-fluid-2 text-xs font-bold uppercase tracking-wide text-gray-500">
              Geographic Routing
            </p>
            <div className="grid grid-cols-1 gap-fluid-2 md:grid-cols-2">
              <GeoHierarchyFields
                side="origin"
                cfg={newConfig}
                onPatch={(patch) => setNewConfig((prev) => ({ ...prev, ...patch }))}
                lookupLoading={lookupLoading.newOrigin}
                onLookup={() => handleLocationLookup({ form: "new", side: "origin" })}
              />
              <GeoHierarchyFields
                side="destination"
                cfg={newConfig}
                onPatch={(patch) => setNewConfig((prev) => ({ ...prev, ...patch }))}
                lookupLoading={lookupLoading.newDestination}
                onLookup={() => handleLocationLookup({ form: "new", side: "destination" })}
              />

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Transport Mode
                </label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newConfig.transportMode}
                  onChange={(e) => {
                    const nextMode = e.target.value;
                    setNewConfig((prev) => {
                      if (!nextMode) {
                        return { ...prev, transportMode: "", shipmentType: "" };
                      }
                      const opts = getShipmentTypeOptionsForMode(nextMode);
                      const stillValid = opts.some((o) => o.value === prev.shipmentType);
                      return {
                        ...prev,
                        transportMode: nextMode,
                        shipmentType: stillValid
                          ? prev.shipmentType
                          : coerceShipmentTypeForTransportMode(nextMode, ""),
                      };
                    });
                  }}
                >
                  <option value="">Select Mode</option>
                  <option value="ROAD">ROAD</option>
                  <option value="SEA">SEA</option>
                  <option value="AIR">AIR</option>
                  <option value="RAIL">RAIL</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Shipment Type
                </label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newConfig.shipmentType}
                  onChange={(e) =>
                    setNewConfig((prev) => ({
                      ...prev,
                      shipmentType: e.target.value,
                    }))
                  }
                  disabled={!newConfig.transportMode}
                >
                  <option value="">
                    {newConfig.transportMode
                      ? "Select Type"
                      : "Select transport mode first"}
                  </option>
                  {getShipmentTypeOptionsForMode(newConfig.transportMode).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-[#0c1629]">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newConfig.paymentMethod}
                  onChange={(e) =>
                    setNewConfig((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  <option>Prepaid</option>
                  <option>Collect</option>
                </select>
              </div>

              <div className="flex items-end pb-2">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-[#0c1629]">
                    <input
                      type="checkbox"
                      checked={newConfig.isRefer}
                      onChange={(e) =>
                        setNewConfig((prev) => ({ ...prev, isRefer: e.target.checked }))
                      }
                    />
                    Refer
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-[#0c1629]">
                    <input
                      id="hazmat"
                      type="checkbox"
                      checked={newConfig.hazmat}
                      onChange={(e) =>
                        setNewConfig((prev) => ({ ...prev, hazmat: e.target.checked }))
                      }
                    />
                    Hazmat Materials
                  </label>
                </div>
              </div>

            </div>
          </div>

          <StrategyWorkflowBlock
            strategies={newConfigStrategies}
            selectionVariantLibrary={selectionLibrary}
            kpiLibraryLoading={kpiLibraryLoading}
            onGoToKpiLibrary={goToKpiLibrary}
            onKpiWeightChange={(strategyId, kpiName, rawValue) =>
              handleKpiWeightChange("new", strategyId, kpiName, rawValue)
            }
            onMustRespondChange={(strategyId, payload) =>
              handleMustRespondChange("new", strategyId, payload)
            }
            onHonourCarrierWorkingHoursChange={(strategyId, checked) =>
              handleHonourCarrierWorkingHoursChange("new", strategyId, checked)
            }
            onAddKpiFromLibrary={(strategyId, def) =>
              handleAddKpiFromLibrary("new", strategyId, def)
            }
            onRemoveKpi={(strategyId, kpiName) =>
              handleRemoveKpi("new", strategyId, kpiName)
            }
            onReorderKpis={(strategyId, orderedKeys) =>
              handleReorderKpis("new", strategyId, orderedKeys)
            }
            onAllocationWeightChange={(strategyId, rank, value) =>
              handleWeightChange("new", strategyId, rank, value)
            }
            onAddAllocationRank={(strategyId) => handleAddRank("new", strategyId)}
            onRemoveAllocationRank={(strategyId, rank) =>
              handleRemoveRank("new", strategyId, rank)
            }
          />

          <SelectionSimulationPanel
            strategies={newConfigStrategies}
            officeCode={newConfig.originatorId || cookieOfficeCode || ""}
            defaultOriginStop={simulationStopFromConfig(newConfig, "origin")}
            defaultDestinationStop={simulationStopFromConfig(newConfig, "destination")}
            isRunning={isRunningSimulation}
            simulationResult={simulationResult}
            simulationLogs={simulationLogs}
            rankedCarriers={simulationRankedCarriers}
            onRunSimulation={(params) => handleRunSimulation({ form: "new", ...params })}
            isConfigDirty
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-fluid-2 border-t border-gray-200 bg-custom-bg-purple py-2">
          {!newKpiSaveReadiness.canSave ? (
            <p className="max-w-2xl text-sm text-amber-700" role="status">
              {newKpiSaveReadiness.message}
            </p>
          ) : (
            <span className="min-w-0 flex-1" aria-hidden="true" />
          )}
          <div className="flex shrink-0 items-center gap-fluid-1">
            <Button variant="outline" text="Cancel" onClick={closeCurrentTab} />
            <Button
              text={isSaving ? "Saving..." : "Save Configuration"}
              onClick={handleCreate}
              disabled={isSaving || !newKpiSaveReadiness.canSave}
            />
          </div>
        </div>
      </div>
  );

  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      <div className="shrink-0 pl-fluid-2">
        <h4 className="text-fluid-lg font-semibold mt-2 text-gray-900 dark:text-acs-dark-text">
          Resource Selection Configurations
        </h4>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-y-2 rounded-md ml-[2] p-fluid-1 shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          {isCarrierKpiTab ? (
            <>
              <FilterButton
                text={carrierKpiBaseFilterLabel}
                options={carrierKpiBaseOptions}
                selectedValue={carrierKpiBaseFilterLabel}
                onSelect={handleCarrierKpiBaseFilterSelect}
              />
              <FilterButton
                text={carrierKpiOfficeFilter}
                options={carrierKpiMeta.officeOptions}
                selectedValue={carrierKpiOfficeFilter}
                onSelect={handleCarrierKpiOfficeFilterSelect}
              />
              <SearchBox
                value={carrierKpiSearchTerm}
                onChange={setCarrierKpiSearchTerm}
                onSearch={() => {}}
                placeholder="Search carriers..."
              />
              <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
                {carrierKpiMeta.loading
                  ? "Loading…"
                  : `${carrierKpiMeta.filteredCount} ${
                      carrierKpiMeta.filteredCount === 1 ? "row" : "rows"
                    }`}
              </span>
            </>
          ) : (
            <>
              <FilterButton
                text={activeStatusFilter}
                options={activeStatusOptions}
                selectedValue={activeStatusFilter}
                onSelect={handleStatusFilterSelect}
              />
              <SearchBox
                value={searchTerm}
                onChange={setSearchTerm}
                onSearch={() => {}}
                placeholder={isKpiLibraryTab ? "Search KPIs..." : "Search configurations..."}
              />
              <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
                {isKpiLibraryTab
                  ? `${filteredKpiCatalog.length} ${filteredKpiCatalog.length === 1 ? "KPI" : "KPIs"}`
                  : `${filteredConfigs.length} ${
                      filteredConfigs.length === 1 ? "configuration" : "configurations"
                    }`}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            text="Refresh"
            onClick={
              isCarrierKpiTab
                ? () => carrierKpiMeta.refresh?.()
                : isKpiLibraryTab
                  ? reloadKpiCatalog
                  : loadConfigs
            }
            disabled={
              isCarrierKpiTab
                ? carrierKpiMeta.loading
                : isKpiLibraryTab
                  ? kpiLibraryLoading
                  : isListLoading
            }
          />
          {!isKpiLibraryTab && !isCarrierKpiTab ? (
          <Button
            onClick={openNewConfigTab}
            text={
              <span className="flex items-center gap-2">
                <Plus size={16} />
                New Configuration
              </span>
            }
          />
          ) : null}
        </div>
      </div>

      <div className="mt-fluid-2 flex min-h-0 flex-1 flex-col">
        <Tabs
          tabs={tabs.map((t) => t.name)}
          tabContents={tabs.map((tab, tabIndex) => {
            if (tab.id === "carrier-kpis") {
              return (
                <div
                  key={tab.id}
                  className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-fluid-1 pb-fluid-2"
                >
                  <CarrierKpiViewPanel
                    catalog={kpiCatalog}
                    isActive={activeTab === tabIndex}
                    baseKpiFilter={carrierKpiBaseFilter}
                    searchTerm={carrierKpiSearchTerm}
                    officeFilter={carrierKpiOfficeFilter}
                    onMetaChange={
                      activeTab === tabIndex ? handleCarrierKpiMetaChange : undefined
                    }
                  />
                </div>
              );
            }
            if (tab.id === "kpi-library") {
              return (
                <div
                  key={tab.id}
                  className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-fluid-1 pb-fluid-2"
                >
                  <KpiLibraryAdmin
                    catalog={filteredKpiCatalog}
                    loading={kpiLibraryLoading}
                    saving={kpiLibrarySaving}
                    error={kpiLibraryError}
                    reload={reloadKpiCatalog}
                    saveBaseKpi={saveBaseKpi}
                    updateBaseKpi={updateBaseKpi}
                    saveSelectionVariant={saveSelectionVariant}
                    deleteSelectionVariant={deleteSelectionVariant}
                    onOpenCarrierKpiTab={openCarrierKpiTab}
                  />
                </div>
              );
            }
            if (tab.id === "resource-selection-list") {
              return (
                <div
                  key={tab.id}
                  className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-fluid-1 pb-fluid-2"
                >
                  {isListLoading ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-surface">
                      <Loader message="Loading resource selection configurations..." />
                    </div>
                  ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
                      <TableSecondary
                        shipmentListStyle
                        columnWidths={[
                          "52px",
                          "16%",
                          "10%",
                          "9%",
                          "8%",
                          "9%",
                          "7%",
                          "11%",
                          "13%",
                          "15%",
                        ]}
                        headers={[
                          <div
                            key="rs-actions-hdr"
                            className="flex items-center justify-center"
                            title="Actions"
                          >
                            <MoreHorizontal
                              size={16}
                              className="text-gray-600 dark:text-gray-300"
                              aria-hidden
                            />
                            <span className="sr-only">Actions</span>
                          </div>,
                          "Name",
                          "Originator",
                          "Status",
                          "Mode",
                          "Type",
                          "Hazmat",
                          "Origin Geo",
                          "Origin Location",
                        ]}
                        data={filteredConfigs}
                        renderRow={(row, rowIndex) => {
                        const rowKey = String(row.id ?? `row-${rowIndex}`);
                        const isOpen = openDropdownRow === rowKey;
                        const deleting = isDeletingConfigId === String(row.id);
                        const toggling = togglingConfigId === String(row.id);
                        const tdData =
                          "border-b border-gray-200 bg-white px-2 text-left text-gray-900 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text sm:px-3";
                        const tdMuted = `${tdData} text-gray-800 dark:text-acs-dark-text`;

                        const actions = [
                          {
                            icon: "Eye",
                            label: "View",
                            color: "blue",
                            action: () => openConfigInTab(row),
                          },
                          {
                            icon: "Edit",
                            label: "Edit",
                            color: "green",
                            action: () => openConfigInTab(row),
                          },
                          {
                            icon: "Trash",
                            label: deleting ? "Deleting..." : "Delete",
                            color: "red",
                            disabled: deleting,
                            action: () => handleDeleteConfig(row),
                          },
                        ];

                        return (
                          <>
                            <td
                              className={`${tdData} text-left align-middle`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                data-rs-action-trigger
                                className={`inline-flex items-center justify-start p-1 text-gray-500 transition-none hover:text-gray-800 dark:text-gray-400 dark:hover:text-acs-dark-text ${
                                  deleting ? "cursor-wait opacity-60" : ""
                                }`}
                                disabled={deleting}
                                onClick={(e) => handleResourceActionClick(rowKey, e)}
                                aria-label="Row actions"
                              >
                                <MoreHorizontal size={18} className="shrink-0" />
                              </button>
                              {isOpen &&
                                ReactDOM.createPortal(
                                  <div
                                    ref={dropdownRef}
                                    style={{
                                      position: "absolute",
                                      top: dropdownPos.top,
                                      left: dropdownPos.left,
                                      zIndex: 9999,
                                    }}
                                  >
                                    <ActionMenu
                                      rowId={rowKey}
                                      isOpen
                                      onClose={() => setOpenDropdownRow(null)}
                                      moduleName="Resource Selection configuration"
                                      actions={actions}
                                    />
                                  </div>,
                                  document.body
                                )}
                            </td>
                            <td className={`${tdData} max-w-0 truncate font-medium`}>
                              {row.name}
                            </td>
                            <td className={`${tdMuted} truncate`}>{row.officeCode}</td>
                            <td className={tdMuted} onClick={(e) => e.stopPropagation()}>
                              <ActiveInactiveToggle
                                checked={row.active !== false}
                                disabled={deleting || toggling}
                                labelId={`cfg-status-${rowKey}`}
                                onChange={(next) => handleConfigActiveToggle(row, next)}
                              />
                            </td>
                            <td className={tdMuted}>{row.mode}</td>
                            <td className={tdMuted}>{row.type}</td>
                            <td className={tdMuted}>{row.hazmat}</td>
                            <td className={`${tdMuted} truncate`}>{row.originGeo}</td>
                            <td className={`${tdMuted} truncate`}>{row.originLocation}</td>
                          </>
                        );
                      }}
                      />
                    </div>
                  )}
                </div>
              );
            }
            if (tab.contentType === "configDetail") {
              return (
                <div key={tab.id} className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
                  {renderConfigDetailPane()}
                </div>
              );
            }
            if (tab.contentType === "newConfig") {
              return (
                <div key={tab.id} className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
                  {renderNewConfigPane()}
                </div>
              );
            }
            return <div key={tab.id || "tab"} className="p-4 text-sm text-gray-500" />;
          })}
          activeTab={activeTab}
          onTabChange={(index) => dispatch(setActiveTab({ index }))}
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
        />
      </div>

      {configPendingDelete ? (
        <ConfirmDeleteModal
          isOpen
          title="Delete configuration"
          headline={`Delete "${
            configPendingDelete.name || `Config ${configPendingDelete.id}`
          }"?`}
          description="This cannot be undone."
          confirmLabel="Delete configuration"
          busy={isDeletingConfigId === String(configPendingDelete.id)}
          error={deleteConfigError}
          onCancel={() => {
            setConfigPendingDelete(null);
            setDeleteConfigError("");
          }}
          onConfirm={confirmDeleteConfig}
        />
      ) : null}

    </div>
  );
};

export default ResourceSelection;
